import React from "react";
import MinusIcon from "../../icons/minus.icon";
import PlusIcon from "../../icons/plus.icon";
import { useTranslation } from "../../core/translation.core";
import productCore from "../../core/product.core";
import { Product } from "../../types/product";

interface CartQtyBtnProps {
  value: number;
  onChange: (quantity: number) => void | Promise<void>;
  min?: number;
  max?: number;
  className?: string;
  product: Product;
}

const GIFT_CARD_MAX_QUANTITY = 9999;

function getDefaultMaxQuantity(product: Product): number {
  if (product.productType === "giftCard") {
    return GIFT_CARD_MAX_QUANTITY;
  }

  return productCore.getSimpleProductInventoryCount(product);
}

const CartQtyBtn = ({ value, onChange, min = 1, max, className = "", product }: CartQtyBtnProps) => {
  const quantityMax = max ?? getDefaultMaxQuantity(product);
  const { t, msg } = useTranslation();
  const isOutOfStock = quantityMax <= 0;

  const handleDecrease = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const handleIncrease = () => {
    if (value < quantityMax) {
      onChange(value + 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(e.target.value);
    if (isNaN(parsed)) return;
    const newValue = Math.max(min, Math.min(parsed, quantityMax));
    onChange(newValue);
  };

  return (
    <div className={`inline-flex items-center border border-gray-200 rounded-md ${className}`}>
      {isOutOfStock ? (
        <span className="text-red-500 text-sm">{t(msg.product.outOfStock)}</span>
      ) : (
        <>
          <button
            type="button"
            className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-600"
            onClick={handleDecrease}
            disabled={value <= min}
          >
            <MinusIcon className="w-4 h-4" />
          </button>

          <input
            type="text"
            value={value}
            onChange={handleInputChange}
            className="w-20 h-10 text-center border-x border-gray-200 focus:outline-none"
          />

          <button
            type="button"
            className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed disabled:hover:text-gray-600"
            onClick={handleIncrease}
            disabled={value >= quantityMax}
          >
            <PlusIcon className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
};

export default CartQtyBtn;
